// Color helpers shared by the speech-visualizer styles.

// rgba()/rgb()/hex -> the same color at a given alpha, for canvas fills.
export function withAlpha(color, a) {
  const m = color.match(/rgba?\(([^)]+)\)/)
  if (m) {
    const [r, g, b] = m[1].split(',').map((n) => n.trim())
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }
  // Hex fallback (#rgb / #rrggbb).
  const hex = color.replace('#', '')
  if (hex.length === 3 || hex.length === 6) {
    const f = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex
    const r = parseInt(f.slice(0, 2), 16)
    const g = parseInt(f.slice(2, 4), 16)
    const b = parseInt(f.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }
  return color
}

// Rotate an rgb(a) color's hue by `deg` and lift saturation/brightness, so the
// visualizer trace is complementary-but-contrasting against the theme accent in
// every theme. Pure JS (no canvas filter) so canvas gl/ shadowColor honor it.
export function shiftHue(color, deg = 150, satMul = 1.4, briMul = 1.12) {
  const m = color.match(/rgba?\(([^)]+)\)/)
  let r
  let g
  let b
  let a = 1
  if (m) {
    const parts = m[1].split(',').map((n) => parseFloat(n.trim()))
    ;[r, g, b] = parts
    if (parts.length > 3) a = parts[3]
  } else {
    const hex = color.replace('#', '')
    const f = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex
    r = parseInt(f.slice(0, 2), 16)
    g = parseInt(f.slice(2, 4), 16)
    b = parseInt(f.slice(4, 6), 16)
  }
  let [h, s, l] = rgbToHsl(r, g, b)
  h = (h + deg / 360) % 1
  s = Math.min(1, s * satMul)
  l = Math.min(1, l * briMul)
  const [nr, ng, nb] = hslToRgb(h, s, l)
  return `rgba(${nr}, ${ng}, ${nb}, ${a})`
}

function rgbToHsl(r, g, b) {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2
  const d = max - min
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }
  return [h, s, l]
}

function hslToRgb(h, s, l) {
  if (s === 0) {
    const v = Math.round(l * 255)
    return [v, v, v]
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue = (t) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  return [
    Math.round(hue(h + 1 / 3) * 255),
    Math.round(hue(h) * 255),
    Math.round(hue(h - 1 / 3) * 255),
  ]
}
